import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, post } from "./client";

/** `/api/v1/workflow`: where every job stands against its SLA, one site's
 * journey through the stages, and the three milestones nothing else records.
 *
 * The stages, their owners and their SLAs are `prv.workflow.stages`; only
 * three stages carry an SLA, so `sla_days` is null on the rest rather than an
 * invented target, and `breach_rate` is null wherever there is nothing to
 * breach or nobody has reached the stage yet. */

export type Owner = "office" | "crew" | "client" | "finance" | (string & {});

export interface StageKpi {
  stage: number;
  label: string;
  owner: Owner;
  sla_days: number | null;
  waiting: number;
  started: number;
  completed: number;
  breached: number;
  breach_rate: number | null;
  median_days: number | null;
}

export interface OverdueJob {
  site: { id: number; name: string };
  stage: string;
  owner: Owner;
  sla_days: number;
  days_over: number;
  due: string | null;
  started: string | null;
}

export interface WorkflowOverview {
  total: number;
  complete: number;
  in_flight: number;
  overdue_count: number;
  by_owner: { owner: Owner; jobs: number }[];
  gaps: { stage: string; jobs: number }[];
  stages: StageKpi[];
  /** Worst first. */
  overdue: OverdueJob[];
  milestone_kinds: string[];
}

export interface StageState {
  stage: number;
  label: string;
  owner: Owner;
  sla_days: number | null;
  started: string | null;
  ended: string | null;
  due: string | null;
  days_over: number | null;
  skipped: boolean;
}

export interface Journey {
  site: { id: number; name: string; status: string };
  states: StageState[];
  milestones: { kind: string; at: string; reference: string | null; note: string | null; recorded_at: string }[];
  milestone_kinds: string[];
}

export interface MilestoneInput {
  kind: string;
  at: string;
  reference: string | null;
  note: string | null;
}

export function useWorkflow() {
  return useQuery<WorkflowOverview, ApiError>({
    queryKey: ["workflow"],
    queryFn: () => get<WorkflowOverview>("/api/v1/workflow"),
  });
}

export function useJourney(siteId: number | null) {
  return useQuery<Journey, ApiError>({
    queryKey: ["workflow", "site", siteId],
    queryFn: () => get<Journey>(`/api/v1/workflow/sites/${siteId}`),
    enabled: siteId !== null,
  });
}

export function useRecordMilestone(siteId: number) {
  const client = useQueryClient();
  return useMutation<Journey, ApiError, MilestoneInput>({
    mutationFn: (body) => post<Journey>(`/api/v1/workflow/sites/${siteId}/milestone`, body),
    onSuccess: (journey) => {
      client.setQueryData(["workflow", "site", siteId], journey);
      void client.invalidateQueries({ queryKey: ["workflow"] });
    },
  });
}
